import { useEffect, useState } from 'react';
import {
  LABEL_MOVIL,
  GARAJE_TIPO_OPCIONES,
  toggleGarajeTipoExclusivo,
  sanitizarGarajeTiposExclusivos,
  TOGGLE_DETALLE_CFG,
  TOGGLE_FLAGS_CFG,
  TOGGLE_SELECTOR_CFG,
  estiloSelBtnMovil,
} from './agenteCamposVinculados';
import InpCampoMovil from './InpCampoMovil';
import './agenteMovilCampos.css';

const overlay = {
  position: 'fixed', inset: 0, zIndex: 13000,
  background: 'rgba(15,23,42,0.45)',
  display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
  padding: 12,
};

const sheet = {
  width: '100%', maxWidth: 420, maxHeight: '85vh', overflow: 'auto',
  background: '#fff', borderRadius: '16px 16px 12px 12px',
  padding: '16px 16px 12px',
  boxShadow: '0 -8px 30px rgba(0,0,0,0.18)',
  fontFamily: 'inherit',
};

/**
 * Modales de campos vinculados (detalle / selector / flags / tipos garaje).
 * `modal` shape:
 *  { tipo: 'detalle'|'selector'|'flags'|'garaje_tipos', key, primera?, plazas? }
 */
export default function ModalVinculosAgente({
  modal,
  valores,
  auxLists,
  onClose,
  onApply,
  onQuitar,
}) {
  if (!modal) return null;
  if (modal.tipo === 'detalle') {
    return (
      <ModalDetalle
        toggleKey={modal.key}
        primera={!!modal.primera}
        valores={valores}
        onClose={onClose}
        onApply={onApply}
        onQuitar={onQuitar}
      />
    );
  }
  if (modal.tipo === 'selector') {
    return (
      <ModalSelector
        toggleKey={modal.key}
        primera={!!modal.primera}
        valores={valores}
        auxLists={auxLists}
        onClose={onClose}
        onApply={onApply}
        onQuitar={onQuitar}
      />
    );
  }
  if (modal.tipo === 'flags') {
    return (
      <ModalFlags
        toggleKey={modal.key}
        primera={!!modal.primera}
        valores={valores}
        onClose={onClose}
        onApply={onApply}
        onQuitar={onQuitar}
      />
    );
  }
  if (modal.tipo === 'garaje_tipos') {
    return (
      <ModalGarajeTipos
        plazas={modal.plazas || 1}
        primera={!!modal.primera}
        valores={valores}
        onClose={onClose}
        onApply={onApply}
        onQuitar={onQuitar}
      />
    );
  }
  return null;
}

function ModalDetalle({ toggleKey, primera, valores, onClose, onApply, onQuitar }) {
  const cfg = TOGGLE_DETALLE_CFG[toggleKey];
  const [val, setVal] = useState(() => String(valores?.[cfg?.detailKey] ?? '').trim());
  if (!cfg) return null;

  const confirmar = () => {
    if (cfg.optional === false && !String(val || '').trim()) return;
    const patch = { [toggleKey]: true, [cfg.detailKey]: String(val || '').trim() };
    if (cfg.anoFinKey) {
      const v = String(val || '').trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(v)) patch[cfg.anoFinKey] = v.slice(0, 4);
      else if (/^\d{4}$/.test(v)) patch[cfg.anoFinKey] = v;
      else patch[cfg.anoFinKey] = '';
    }
    onApply?.(patch, { encadenarGarajeTipos: !!cfg.encadenarTipos && primera });
  };

  const cancelar = () => {
    if (primera && !String(valores?.[cfg.detailKey] || '').trim()) {
      onQuitar?.(toggleKey);
      return;
    }
    onClose?.();
  };

  return (
    <div style={overlay} onClick={cancelar} role="presentation">
      <div className="agente-movil-campos" style={sheet} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 17, fontWeight: 800, color: '#0f172a', marginBottom: 6 }}>{cfg.title}</div>
        {cfg.hint && <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10, lineHeight: 1.4 }}>{cfg.hint}</div>}
        <div className="agente-f-label" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: LABEL_MOVIL, marginBottom: 6 }}>
          {cfg.label}
        </div>
        <InpCampoMovil
          type={cfg.inputType === 'date' ? 'date' : cfg.inputType === 'number' ? 'number' : 'text'}
          value={val}
          placeholder={cfg.placeholder || ''}
          onChange={(e) => setVal(e.target.value)}
          style={{ marginBottom: 14 }}
          autoFocus
        />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <button type="button" onClick={confirmar} style={{ ...estiloSelBtnMovil(true), width: '100%' }}>Aceptar</button>
          <button type="button" onClick={() => onQuitar?.(toggleKey)} style={{ ...estiloSelBtnMovil(false), width: '100%', color: '#b91c1c', borderColor: '#fecaca' }}>
            {cfg.quitarLabel || 'Quitar'}
          </button>
          <button type="button" onClick={cancelar} style={{ ...estiloSelBtnMovil(false), width: '100%' }}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

function ModalSelector({ toggleKey, primera, valores, auxLists, onClose, onApply, onQuitar }) {
  const cfg = TOGGLE_SELECTOR_CFG[toggleKey];
  if (!cfg) return null;
  const rows = auxLists?.[cfg.auxSlug] || [];
  const actual = valores?.[cfg.detailKey] ?? '';

  const cancelar = () => {
    if (primera && (actual == null || String(actual).trim() === '')) {
      onQuitar?.(toggleKey);
      return;
    }
    onClose?.();
  };

  return (
    <div style={overlay} onClick={cancelar} role="presentation">
      <div style={sheet} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 17, fontWeight: 800, color: '#0f172a', marginBottom: 12 }}>{cfg.title}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
          {rows.length === 0 && (
            <div style={{ fontSize: 13, color: '#64748b' }}>No hay opciones configuradas.</div>
          )}
          {rows.map((row) => {
            const activo = String(actual) === String(row.id);
            return (
              <button
                key={row.id}
                type="button"
                onClick={() => onApply?.({ [toggleKey]: true, [cfg.detailKey]: row.id })}
                style={{ ...estiloSelBtnMovil(activo), width: '100%', fontSize: 15 }}
              >
                {row.nombre}
              </button>
            );
          })}
        </div>
        <button type="button" onClick={() => onQuitar?.(toggleKey)} style={{ ...estiloSelBtnMovil(false), width: '100%', color: '#b91c1c', borderColor: '#fecaca', marginBottom: 8 }}>
          {cfg.quitarLabel || 'Quitar'}
        </button>
        <button type="button" onClick={cancelar} style={{ ...estiloSelBtnMovil(false), width: '100%' }}>Cancelar</button>
      </div>
    </div>
  );
}

function ModalFlags({ toggleKey, primera, valores, onClose, onApply, onQuitar }) {
  const cfg = TOGGLE_FLAGS_CFG[toggleKey];
  const [flags, setFlags] = useState(() => {
    const o = {};
    (cfg?.fields || []).forEach((f) => {
      const v = valores?.[f.key];
      o[f.key] = v === true || v === 'true' || v === 1 || v === '1';
      if (f.obs) o[`${f.key}_obs`] = String(valores?.[`${f.key}_obs`] || '');
    });
    return o;
  });
  if (!cfg) return null;

  const cancelar = () => {
    if (primera) {
      onQuitar?.(toggleKey);
      return;
    }
    onClose?.();
  };

  const toggleFlag = (f) => {
    setFlags((prev) => {
      const nextOn = !prev[f.key];
      const next = { ...prev, [f.key]: nextOn };
      if (!nextOn && f.obs) next[`${f.key}_obs`] = '';
      if (nextOn && f.exclusive) {
        (cfg.fields || []).forEach((other) => {
          if (other.key === f.key) return;
          next[other.key] = false;
          if (other.obs) next[`${other.key}_obs`] = '';
        });
      } else if (nextOn && !f.exclusive) {
        (cfg.fields || []).forEach((other) => {
          if (!other.exclusive) return;
          next[other.key] = false;
          if (other.obs) next[`${other.key}_obs`] = '';
        });
      }
      return next;
    });
  };

  const aceptar = () => {
    if (cfg.requireOne && !(cfg.fields || []).some((f) => flags[f.key])) {
      window.alert('Marca al menos una opción: DPMT, Serv. Tránsito, Serv. Protección o No afectado.');
      return;
    }
    const patch = { [toggleKey]: true };
    (cfg.fields || []).forEach((f) => {
      patch[f.key] = !!flags[f.key];
      if (f.obs) patch[`${f.key}_obs`] = flags[f.key] ? String(flags[`${f.key}_obs`] || '').trim() : '';
    });
    onApply?.(patch);
  };

  return (
    <div style={overlay} onClick={cancelar} role="presentation">
      <div style={sheet} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 17, fontWeight: 800, color: '#0f172a', marginBottom: 12 }}>{cfg.title}</div>
        {cfg.requireOne && (
          <div style={{ fontSize: 13, color: '#64748b', marginBottom: 10, lineHeight: 1.35 }}>
            Obligatorio: marca al menos una opción (DPMT, Serv. Tránsito, Serv. Protección o No afectado).
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
          {cfg.fields.map((f) => {
            const showObs = f.obs && flags[f.key];
            // CRM: Obs. de «No afectado» al lado del botón; resto debajo (como APP).
            if (f.exclusive) {
              return (
                <div key={f.key} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button
                    type="button"
                    onClick={() => toggleFlag(f)}
                    style={{
                      ...estiloSelBtnMovil(!!flags[f.key]),
                      flex: showObs ? '0 1 auto' : '1 1 100%',
                      width: showObs ? 'auto' : '100%',
                      fontSize: 15,
                    }}
                  >
                    {f.label}
                  </button>
                  {showObs && (
                    <input
                      type="text"
                      value={flags[`${f.key}_obs`] || ''}
                      onChange={(e) => setFlags((prev) => ({ ...prev, [`${f.key}_obs`]: e.target.value }))}
                      placeholder="Obs."
                      style={{
                        flex: 1,
                        minWidth: 0,
                        boxSizing: 'border-box',
                        padding: '10px 12px',
                        borderRadius: 10,
                        border: '1px solid #cbd5e1',
                        fontSize: 14,
                      }}
                    />
                  )}
                </div>
              );
            }
            return (
              <div key={f.key}>
                <button
                  type="button"
                  onClick={() => toggleFlag(f)}
                  style={{ ...estiloSelBtnMovil(!!flags[f.key]), width: '100%', fontSize: 15 }}
                >
                  {f.label}
                </button>
                {showObs && (
                  <input
                    type="text"
                    value={flags[`${f.key}_obs`] || ''}
                    onChange={(e) => setFlags((prev) => ({ ...prev, [`${f.key}_obs`]: e.target.value }))}
                    placeholder="Obs."
                    style={{
                      width: '100%', marginTop: 6, boxSizing: 'border-box',
                      padding: '10px 12px', borderRadius: 10, border: '1px solid #cbd5e1',
                      fontSize: 14,
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
        <button
          type="button"
          onClick={aceptar}
          style={{ ...estiloSelBtnMovil(true), width: '100%', marginBottom: 8 }}
        >
          Aceptar
        </button>
        <button type="button" onClick={() => onQuitar?.(toggleKey)} style={{ ...estiloSelBtnMovil(false), width: '100%', color: '#b91c1c', borderColor: '#fecaca', marginBottom: 8 }}>
          {cfg.quitarLabel || 'Quitar'}
        </button>
        <button type="button" onClick={cancelar} style={{ ...estiloSelBtnMovil(false), width: '100%' }}>Cancelar</button>
      </div>
    </div>
  );
}

function ModalGarajeTipos({ plazas, primera, valores, onClose, onApply, onQuitar }) {
  const n = Math.max(1, parseInt(String(plazas), 10) || 1);
  const [idx, setIdx] = useState(0);
  const [numGaraje, setNumGaraje] = useState(() => String(valores?.num_garaje || '').trim());
  const [borrador, setBorrador] = useState(() => {
    const det = Array.isArray(valores?.garajes_plazas_detalle) ? valores.garajes_plazas_detalle : [];
    const out = [];
    for (let i = 0; i < n; i++) {
      const tipos = det[i]?.tipos;
      out.push(sanitizarGarajeTiposExclusivos(
        Array.isArray(tipos) ? tipos.map((t) => String(t).toLowerCase()) : []
      ));
    }
    return out;
  });

  useEffect(() => {
    setBorrador((prev) => {
      const next = prev.slice(0, n);
      while (next.length < n) next.push([]);
      return next;
    });
  }, [n]);

  const seleccionados = borrador[idx] || [];
  const toggleTipo = (val) => {
    setBorrador((prev) => {
      const next = prev.map((arr) => arr.slice());
      next[idx] = toggleGarajeTipoExclusivo(next[idx] || [], val);
      return next;
    });
  };

  const confirmarPaso = () => {
    if (!seleccionados.length) {
      window.alert('Selecciona al menos un tipo');
      return;
    }
    if (idx + 1 < n) {
      setIdx((i) => i + 1);
      return;
    }
    const union = [];
    const seen = new Set();
    borrador.forEach((arr) => {
      (arr || []).forEach((t) => {
        const lab = GARAJE_TIPO_OPCIONES.find((o) => o.toLowerCase() === String(t).toLowerCase()) || t;
        if (!seen.has(String(lab).toLowerCase())) {
          seen.add(String(lab).toLowerCase());
          union.push(lab);
        }
      });
    });
    onApply?.({
      eq_garaje: true,
      num_plazas_garaje: n,
      num_garaje: numGaraje,
      garaje_tipos: union,
      garajes_plazas_detalle: borrador.map((tipos) => ({ tipos: tipos.slice() })),
    });
  };

  const cancelar = () => {
    if (primera && !(Array.isArray(valores?.garaje_tipos) && valores.garaje_tipos.length)) {
      onQuitar?.('eq_garaje');
      return;
    }
    onClose?.();
  };

  return (
    <div style={overlay} onClick={cancelar} role="presentation">
      <div className="agente-movil-campos" style={sheet} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 17, fontWeight: 800, color: '#0f172a', marginBottom: 4 }}>
          {n > 1 ? `Garaje ${idx + 1} de ${n}` : 'Garaje'}
        </div>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
          {n > 1 ? `Elige el tipo del garaje ${idx + 1} (puedes marcar varios)` : 'Elige el tipo (puedes marcar varios)'}
        </div>
        {idx === 0 && (
          <>
            <div className="agente-f-label" style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: LABEL_MOVIL, marginBottom: 6 }}>
              Nº garaje
            </div>
            <InpCampoMovil
              type="text"
              value={numGaraje}
              onChange={(e) => setNumGaraje(e.target.value)}
              placeholder="Ej: 45 / G-2 (opcional)"
              style={{ marginBottom: 12 }}
            />
          </>
        )}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
          {GARAJE_TIPO_OPCIONES.map((op) => {
            const val = op.toLowerCase();
            const activo = seleccionados.includes(val);
            return (
              <button
                key={op}
                type="button"
                onClick={() => toggleTipo(val)}
                style={{
                  ...estiloSelBtnMovil(activo, { fontSize: 14 }),
                  flex: '1 1 calc(50% - 8px)',
                  borderRadius: 20,
                  padding: '10px 12px',
                }}
              >
                {op}
              </button>
            );
          })}
        </div>
        <button type="button" onClick={confirmarPaso} style={{ ...estiloSelBtnMovil(true), width: '100%', marginBottom: 8 }}>
          {idx + 1 < n ? 'Siguiente' : 'Aceptar'}
        </button>
        <button
          type="button"
          onClick={() => onQuitar?.('eq_garaje')}
          style={{ ...estiloSelBtnMovil(false), width: '100%', color: '#b91c1c', borderColor: '#fecaca', marginBottom: 8 }}
        >
          Quitar garaje
        </button>
        <button type="button" onClick={cancelar} style={{ ...estiloSelBtnMovil(false), width: '100%' }}>Cancelar</button>
      </div>
    </div>
  );
}
